import { useQueryClient } from '@tanstack/react-query'
import { ArrowRightIcon, InfoIcon, SearchIcon } from 'lucide-react'
import { useState } from 'react'

import employersEmployees from 'rails_routes/Employers/EmployeesPaths'
import { Button, Checkbox, FilterButton, FiltersDropdown, Pagination, Switch, Table, TextButton, TextInput, Tooltip, TooltipProvider, useBreakpoint } from 'slab'
import {
  getGetEmployersOffboardingEmployeesToOffboardQueryKey,
  usePatchEmployersOffboardingEmployeesEmployeeId,
} from 'utils/web_api'

import { getEmployeeStatusBadge } from './employeeStatusUtils'
import { MuteAction } from './MuteAction'
import { useSelectEmployees } from './useSelectEmployees'

const STATUS_FILTER_OPTIONS = [
  { value: null as string | null, label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'payroll_deactivated', label: 'Payroll deactivated' },
  { value: 'on_leave', label: 'On leave' },
]

export function SelectEmployeesStep () {
  const {
    pageIndex,
    setPageIndex,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    disableFiltering,
    statusFilter,
    setStatusFilter,
    isLoading,
    hasLoadedAllEmployees,
    currentPageEmployees,
    totalRows,
    selectedCount,
    isConnectedToPayroll,
    allOnPageSelected,
    someOnPageSelected,
    allEmployeesSelected,
    selectableCount,
    isEmployeeSelected,
    toggleEmployeeSelection,
    clearAllSelections,
    handleSelectAllOnPage,
    handleSelectAllEmployees,
    handleNext,
    handleToggleFiltering,
    handleShowAllEmployees,
    PAGE_SIZE,
  } = useSelectEmployees()

  const { breakpointIsAtLeast } = useBreakpoint()
  const [filterOpen, setFilterOpen] = useState(false)
  const queryClient = useQueryClient()
  const muteMutation = usePatchEmployersOffboardingEmployeesEmployeeId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetEmployersOffboardingEmployeesToOffboardQueryKey(),
        })
      },
    },
  })

  return (
    <div>
      <div className='mb-6'>
        <h1 className='heading-lg mb-2'>Select employees to offboard</h1>
        <p className='body text-gray-600'>
          Select the employees you want to offboard. You can select all employees or choose specific ones.
        </p>
      </div>

      <div className='bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden'>
        {/* Table toolbar */}
        <div className='px-6 py-4 flex flex-col gap-4'>
          <div className='flex flex-wrap items-center gap-3'>
            <div className='flex-1 min-w-0 md:flex-initial md:w-64'>
              <TextInput
                leadingIcon={SearchIcon}
                placeholder='Find employees'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {isConnectedToPayroll && (
              <FiltersDropdown
                align='end'
                onOpenChange={setFilterOpen}
                open={filterOpen}
                trigger={({ open }) => (
                  <FilterButton
                    filtersCount={statusFilter ? 1 : 0}
                    open={open}
                    size='small'
                    variant='secondary'
                  />
                )}
                width={breakpointIsAtLeast('xs') ? 'auto' : 'full'}
              >
                <FiltersDropdown.Filters>
                  <div className='text-xs font-medium text-gray-700'>Filter by status</div>
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <button
                      key={option.value ?? 'all'}
                      type='button'
                      className='flex w-full cursor-pointer items-center py-1.5 text-left text-sm hover:bg-gray-50'
                      onClick={() => {
                        setStatusFilter(option.value)
                        setFilterOpen(false)
                      }}
                    >
                      <span className='mr-2 w-4'>
                        {statusFilter === option.value && '✓'}
                      </span>
                      {option.label}
                    </button>
                  ))}
                </FiltersDropdown.Filters>
              </FiltersDropdown>
            )}
            {isConnectedToPayroll && (
              <div className='ml-auto'>
                <Switch
                  checked={disableFiltering}
                  onChange={() => handleToggleFiltering()}
                  label='Show all employees'
                />
              </div>
            )}
          </div>
        </div>

        {/* Desktop table */}
        <div className='hidden md:block'>
          <Table width='full'>
            <Table.Head>
              <Table.Row>
                <Table.SelectionColumnHeader
                  selected={allOnPageSelected}
                  indeterminate={someOnPageSelected}
                  onSelect={handleSelectAllOnPage}
                  disabled={isLoading}
                />
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                {isConnectedToPayroll && (
                  <Table.ColumnHeader>Payroll status</Table.ColumnHeader>
                )}
                {isConnectedToPayroll && (
                  <Table.ColumnHeader>
                    <div className='inline-flex items-center gap-1.5'>
                      <span>On leave until</span>
                      <TooltipProvider>
                        <Tooltip
                          content='Mark an employee as on leave to snooze homepage notifications and hide from this list until a specific date'
                          side='top'
                          align='center'
                        >
                          <InfoIcon size={16} className='text-gray-400' />
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </Table.ColumnHeader>
                )}
                <Table.ColumnHeader>Start date</Table.ColumnHeader>
                {isConnectedToPayroll && (
                  <Table.ColumnHeader>End date</Table.ColumnHeader>
                )}
                <Table.ColumnHeader>Personal email</Table.ColumnHeader>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {isLoading
                ? (
                  <Table.Row>
                    <Table.Cell
                      colSpan={isConnectedToPayroll ? 7 : 5}
                      align='center'
                    >
                      <div className='py-8 text-gray-500'>Loading employees...</div>
                    </Table.Cell>
                  </Table.Row>
                  )
                : currentPageEmployees.length === 0
                  ? (
                    <Table.Row>
                      <Table.Cell colSpan={isConnectedToPayroll ? 7 : 5} align='center'>
                        <div className='py-8 text-gray-500'>
                          {debouncedSearchQuery
                            ? 'No employees match your search'
                            : isConnectedToPayroll && !disableFiltering
                              ? (
                                <>
                                  No employees are pending offboarding.{' '}
                                  <TextButton
                                    color='gray'
                                    onClick={handleShowAllEmployees}
                                  >
                                    Show all employees
                                  </TextButton>
                                </>
                                )
                              : isConnectedToPayroll
                                ? 'No employees found.'
                                : (
                                  <>
                                    No employees found. Please add an employee on the{' '}
                                    <a href={employersEmployees.index()} className='text-blue-600 hover:text-blue-700 underline'>
                                      Employees page
                                    </a>{' '}
                                    to get started.
                                  </>
                                  )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                    )
                  : (
                      currentPageEmployees.map(employee => {
                        const statusBadge = getEmployeeStatusBadge(
                          employee.employment_status,
                          employee.end_date ?? null,
                          employee.silence_pending_termination_until ?? null
                        )
                        return (
                          <Table.Row key={employee.id}>
                            <Table.SelectionCell
                              selected={isEmployeeSelected(employee.id)}
                              onSelect={() => toggleEmployeeSelection(employee.id)}
                              accessibleLabel={`Select ${employee.first_name} ${employee.last_name}`}
                            />
                            <Table.Cell>
                              {employee.first_name} {employee.last_name}
                            </Table.Cell>
                            {isConnectedToPayroll && (
                              <Table.Cell>
                                <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${statusBadge.classes}`}>
                                  {statusBadge.label}
                                </span>
                              </Table.Cell>)}
                            {isConnectedToPayroll && (
                              <Table.Cell>
                                <MuteAction
                                  employee={employee}
                                  onMute={async (employeeId, date) => {
                                    await muteMutation.mutateAsync({
                                      employeeId,
                                      data: { silence_pending_termination_until: date },
                                    })
                                  }}
                                  onUnmute={(employeeId) => {
                                    muteMutation.mutate({
                                      employeeId,
                                      data: { silence_pending_termination_until: null },
                                    })
                                  }}
                                  disabled={muteMutation.isPending}
                                  isPayrollDeactivated={employee.employment_status === 'pending_deactivation'}
                                />
                              </Table.Cell>
                            )}
                            <Table.Cell>
                              {employee.start_date
                                ? new Date(employee.start_date).toLocaleDateString()
                                : '—'}
                            </Table.Cell>
                            {isConnectedToPayroll && (
                              <Table.Cell>
                                {employee.end_date
                                  ? new Date(employee.end_date).toLocaleDateString()
                                  : '—'}
                              </Table.Cell>
                            )}
                            <Table.Cell>
                              {employee.personal_email ?? '—'}
                            </Table.Cell>
                          </Table.Row>
                        )
                      })
                    )}
            </Table.Body>
          </Table>
        </div>

        {/* Mobile card view */}
        <div className='md:hidden'>
          {isLoading
            ? (
              <div className='py-8 text-center text-gray-500'>Loading employees...</div>
              )
            : currentPageEmployees.length === 0
              ? (
                <div className='py-8 px-4 text-center text-gray-500'>
                  {debouncedSearchQuery
                    ? 'No employees match your search.'
                    : isConnectedToPayroll && !disableFiltering
                      ? (
                        <span className='flex flex-wrap items-center gap-2'>
                          No employees are pending offboarding.{' '}
                          <TextButton
                            color='gray'
                            onClick={handleShowAllEmployees}
                          >
                            All employees
                          </TextButton>
                        </span>
                        )
                      : isConnectedToPayroll
                        ? 'No employees found.'
                        : (
                          <>
                            No employees found. Please add an employee on the{' '}
                            <a href={employersEmployees.index()} className='text-blue-600 hover:text-blue-700 underline'>
                              Employees page
                            </a>{' '}
                            to get started.
                          </>
                          )}
                </div>
                )
              : (
                <div className='divide-y divide-gray-200'>
                  {/* Select all on page header */}
                  <div className='px-4 py-3 bg-gray-50 flex items-center gap-3'>
                    <Checkbox
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected}
                      onChange={handleSelectAllOnPage}
                      aria-label='Select all on page'
                    />
                    <span className='body-sm text-gray-600'>
                      {allOnPageSelected ? 'Deselect all on page' : 'Select all on page'}
                    </span>
                  </div>
                  {currentPageEmployees.map(employee => {
                    const statusBadge = getEmployeeStatusBadge(
                      employee.employment_status,
                      employee.end_date ?? null,
                      employee.silence_pending_termination_until ?? null
                    )
                    const selected = isEmployeeSelected(employee.id)
                    const isMuted = employee.is_muted
                    return (
                      <div
                        key={employee.id}
                        className='px-4 py-4'
                      >
                        {/* Row: checkbox + name + status */}
                        <div className='flex items-start gap-3'>
                          <div className='mt-0.5'>
                            <Checkbox
                              checked={selected}
                              onChange={() => !isMuted && toggleEmployeeSelection(employee.id)}
                              aria-label={`Select ${employee.first_name} ${employee.last_name}`}
                            />
                          </div>
                          <div className='flex-1 min-w-0'>
                            <div className='flex items-center justify-between gap-2'>
                              <span className='body-bold truncate'>
                                {employee.first_name} {employee.last_name}
                              </span>
                              {isConnectedToPayroll && (
                                <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium flex-shrink-0 ${statusBadge.classes}`}>
                                  {statusBadge.label}
                                </span>
                              )}
                            </div>
                            <div className='mt-2 flex flex-col gap-1'>
                              {isConnectedToPayroll && (
                                <div className='flex justify-between body-sm'>
                                  <span className='text-gray-500'>On leave until</span>
                                  <MuteAction
                                    employee={employee}
                                    onMute={async (employeeId, date) => {
                                      await muteMutation.mutateAsync({
                                        employeeId,
                                        data: { silence_pending_termination_until: date },
                                      })
                                    }}
                                    onUnmute={(employeeId) => {
                                      muteMutation.mutate({
                                        employeeId,
                                        data: { silence_pending_termination_until: null },
                                      })
                                    }}
                                    disabled={muteMutation.isPending}
                                    isPayrollDeactivated={employee.employment_status === 'pending_deactivation'}
                                  />
                                </div>
                              )}
                              <div className='flex justify-between body-sm'>
                                <span className='text-gray-500'>Start date</span>
                                <span className='text-gray-900'>
                                  {employee.start_date
                                    ? new Date(employee.start_date).toLocaleDateString()
                                    : '—'}
                                </span>
                              </div>
                              {isConnectedToPayroll && (
                                <div className='flex justify-between body-sm'>
                                  <span className='text-gray-500'>End date</span>
                                  <span className='text-gray-900'>
                                    {employee.end_date
                                      ? new Date(employee.end_date).toLocaleDateString()
                                      : '—'}
                                  </span>
                                </div>
                              )}
                              <div className='flex justify-between gap-4 body-sm'>
                                <span className='text-gray-500 whitespace-nowrap'>Personal email</span>
                                <span className='text-gray-900 truncate min-w-0'>
                                  {employee.personal_email ?? '—'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                )}
        </div>

        {/* Bottom bar with count, select all/deselect all, and pagination */}
        <div className='flex flex-col gap-3 py-4 px-4 md:flex-row md:items-center md:justify-between md:px-6'>
          <div className='flex items-center gap-2'>
            {selectedCount > 0 && (
              <span className='body-sm-bold text-gray-900'>
                {selectedCount} of {totalRows} selected
              </span>
            )}
            {selectedCount > 0 && hasLoadedAllEmployees && !allEmployeesSelected && selectedCount < selectableCount && (
              <span className='body-sm flex items-center gap-1.5'>
                <span className='text-gray-400'>•</span>
                <TextButton color='gray' onClick={handleSelectAllEmployees}>
                  Select all {selectableCount} employees
                </TextButton>
              </span>
            )}
            {allEmployeesSelected && (
              <span className='body-sm flex items-center gap-1.5'>
                <span className='text-gray-400'>•</span>
                <TextButton color='gray' onClick={clearAllSelections}>
                  Deselect all
                </TextButton>
              </span>
            )}
          </div>
          <div className='flex justify-end'>
            <Pagination
              pageIndex={pageIndex}
              perPage={PAGE_SIZE}
              totalRows={totalRows}
              onPageChange={setPageIndex}
            />
          </div>
        </div>
      </div>

      <div className='mt-6 flex justify-end'>
        <Button
          variant='primary'
          size='large'
          disabled={selectedCount === 0 || !hasLoadedAllEmployees}
          onClick={handleNext}
          rightIcon={ArrowRightIcon}
        >
          {!hasLoadedAllEmployees
            ? 'Loading your employees...'
            : selectedCount === 0
              ? 'Continue'
              : `Continue with ${selectedCount} employee${selectedCount === 1 ? '' : 's'}`}
        </Button>
      </div>
    </div>
  )
}
